import _ from "lodash";
import { FileExtendedDetails } from "../commonTypes/fileupload.types";
import { FormField } from "../commonTypes/formField.types";
import { ValidationResult } from "../commonTypes/validation.types";
import { SelectBoxOption } from "../components/FormControls/SelectBox";

export function fileDetail2SelectBoxOption(
  fileDetail: FileExtendedDetails
): SelectBoxOption {
  return {
    key: fileDetail.FileId,
    text: fileDetail.OriginalFilename,
  };
}

export function getErrorMessageFromValidationResult(
  validationResults: ValidationResult[]
) {
  return function (formField: FormField<any>): string {
    if (!formField.isTouched) {
      return "";
    }
    const result = getValidationResultByFieldName(
      validationResults,
      formField.fieldName
    );
    return result ? result.ValidationText : "";
  };
}

export function getErrorMessageByFieldName(
  validationResults: ValidationResult[] = [],
  fieldName: string
): string {
  const validation = getValidationResultByFieldName(
    validationResults,
    fieldName
  );
  if (validation) {
    return validation.ValidationText;
  }
  return "";
}

export function getErrorMessageByFieldNameAfterHitSummary(
  validationResults: ValidationResult[] = [],
  fieldName: string,
  hasHitSummary: boolean
) {
  if (!hasHitSummary) {
    return "";
  }
  return getErrorMessageByFieldName(validationResults, fieldName);
}

export function getValidationResultByFieldName(
  validationResults: ValidationResult[] = [],
  fieldName: string
): ValidationResult {
  if (!validationResults) {
    return { FieldName: "", ValidationText: "" };
  }
  fieldName = fieldName
    .replace("CurrentResearchOrder.", "")
    .replace("StatisticsOrder.", "");
  return validationResults.filter(
    (validationResult) => validationResult.FieldName === fieldName
  )[0];
}

export function areAllFieldsErrorFree(
  validationResults: ValidationResult[],
  ...fieldNames: string[]
): boolean {
  let isValid = true;
  for (const field of fieldNames) {
    const errorMsg = getErrorMessageByFieldName(validationResults, field);
    if (errorMsg !== "") {
      isValid = false;
      break;
    }
  }
  return isValid;
}

export function setFocusOnFirstElement(classNames) {
  const divs = document.getElementsByClassName(classNames);
  if (!divs || divs.length === 0) return;
  const firstDiv = divs[0];
  const inputElems: NodeListOf<HTMLElement> | NodeListOf<HTMLInputElement> =
    firstDiv.querySelectorAll("button, input");
  if (inputElems && inputElems.length > 0) {
    inputElems[0].focus();
    return;
  }
}

export function setFocusOnElementById(elementId: string) {
  const element = document.getElementById(elementId) as HTMLElement;
  if (!element) return;
  element.focus();
}

export function pushTagManagerEvent(
  event: string,
  category: string,
  action: string,
  label?: string,
  customVariable?: boolean,
  customVariableValue?: string,
  menu_item?: string,
  link_clicked_name?: string,
  link_clicked_url?: string,
  breadcrumb_name?: string,
  breadcrumb_full_path?: string
): void {
  let dataLayer = (window as any).dataLayer;
  if (dataLayer === undefined) return; //not available in e-services
  let obj = {
    event,
    eventInfo: {
      category,
      action,
    },
  } as DataLayerObject;
  if (label) {
    obj.eventInfo.label = label;
  }

  if (menu_item) {
    obj["menu_item"] = menu_item;
  }
  if (link_clicked_name && link_clicked_url) {
    obj["link_clicked_name"] = link_clicked_name;
    obj["link_clicked_url"] = link_clicked_url;
  }

  if (breadcrumb_name && breadcrumb_full_path) {
    obj["breadcrumb_name"] = breadcrumb_name;
    obj["breadcrumb_full_path"] = breadcrumb_full_path;
  }

  if (customVariable) {
    let customobj = {
      event: event,
      eventInfo: {
        category: category,
        action: action,
        label: label,
        video_title: customVariableValue,
      },
    };

    dataLayer.push(customobj);
  } else {
    dataLayer.push(obj);
  }
}
export function getSelectedItemsFromMap(map: Map<string, boolean>): string[] {
  let s: string[] = [];
  (map || new Map<string, boolean>()).forEach((k, v) => {
    if (k) s.push(v);
  });
  return s;
}

export interface DataLayerObject {
  event: string;
  eventInfo: EventInfo;
}

export interface EventInfo {
  category: string;
  action: string;
  label?: string;
}

export default function getTextTrimedLength(text, maxLength) {
  if (maxLength <= 0) return "";
  if (maxLength >= text.length) return text;

  let truncatedText = text.substr(0, maxLength);
  if (text[maxLength] === " ") return truncatedText; //next letter past limit is ' '

  let lastSign = "";
  let count = 0;
  do {
    count++;
    if (count >= maxLength) return "";
    lastSign = truncatedText[truncatedText.length - count];
  } while (lastSign != " ");
  truncatedText = truncatedText.substr(0, maxLength - count);
  return truncatedText;
}

export function setInnerHtml(html: string) {
  return { __html: html };
}

export function stringWithLineBreak(string: string) {
  if (!string) return string;
  return string
    .split("\r\n")
    .join("<br>")
    .split("\n")
    .join("<br>")
    .split("\r")
    .join("<br>");
}

//https://coderwall.com/p/flonoa/simple-string-format-in-javascript
export function formatString(input: string, ...args: string[]) {
  if (!input) return input;
  if (!input.match(/^(?:(?:(?:[^{}]|(?:\{\{)|(?:\}\}))+)|(?:\{[0-9]+\}))+$/)) {
    throw new Error("invalid format string.");
  }
  return input.replace(
    /((?:[^{}]|(?:\{\{)|(?:\}\}))+)|(?:\{([0-9]+)\})/g,
    (m, str, index) => {
      if (str) {
        return str.replace(/(?:{{)|(?:}})/g, (m) => m[0]);
      } else {
        if (index >= args.length) {
          throw new Error("argument index is out of range in format");
        }
        return args[index];
      }
    }
  );
}

export function parseInputToInt(input: string): number {
  let val = parseInt(input);
  val = isNaN(val) ? 0 : val;
  return val;
}

export function isNumeric(input: any): boolean {
  return !isNaN(input);
}

//https://www.javascripttutorial.net/dom/css/check-if-an-element-is-visible-in-the-viewport/
//Archive: https://archive.md/aWMaq
export function isInViewport(el: HTMLElement) {
  if (!el) return false;

  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function getCookie(key) {
  var b = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)");
  return b ? b.pop() : "";
}

export function smoothScroll(elem, offset = 0) {
  return new Promise((resolve, reject) => {
    var jump = elem.getBoundingClientRect();
    window.scrollTo({
      top: jump.top + window.scrollY - offset,
      left: jump.left,
      behavior: "smooth",
    });
    window.addEventListener("scroll", _.debounce(resolve, 100));
  });
}
export function elementReady(selector) {
  return new Promise((resolve, reject) => {
    let el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    new MutationObserver((mutationRecords, observer) => {
      // Query for elements matching the specified selector
      Array.from(document.querySelectorAll(selector)).forEach((element) => {
        resolve(element);
        //Once we have resolved we don't need the observer anymore.
        observer.disconnect();
      });
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  });
}
export function contains(arr, element, propertyName) {
  for (var i = 0; i < arr.length; i++) {
    if (arr[i][propertyName] === element || arr[i] === element) {
      return true;
    }
  }
  return false;
}
